import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI, BASE_WITHOUT_API } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Phone } from 'lucide-react';
import { accountAccessNotice } from '../utils/accountAccess';

function Login() {
  const navigate = useNavigate();
  const [loginMethod, setLoginMethod] = useState('email');
  const [formData, setFormData] = useState({ identifier: '', password: '' });
  const [requiresOTP, setRequiresOTP] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpForDev, setOtpForDev] = useState('');
  const [otpIdentifier, setOtpIdentifier] = useState('');
  const [otpChallengeId, setOtpChallengeId] = useState('');
  const [otpScope, setOtpScope] = useState('');
  const [otpDeliveryTargets, setOtpDeliveryTargets] = useState([]);
  const [authMode, setAuthMode] = useState('login');
  const [resetData, setResetData] = useState({
    identifier: '',
    otp: '',
    new_password: '',
    confirm_password: '',
  });
  const [resetIdentifier, setResetIdentifier] = useState('');
  const [resetOtpForDev, setResetOtpForDev] = useState('');
  const [loading, setLoading] = useState(false);
  const accessNotice = accountAccessNotice(new URLSearchParams(window.location.search).get('reason'));

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login(formData);
      
      if (response.data.requires_otp) {
        setRequiresOTP(true);
        setOtpIdentifier(response.data.identifier || formData.identifier);
        setOtpChallengeId(response.data.challenge_id || '');
        setOtpScope(response.data.mfa_scope || '');
        setOtpDeliveryTargets(response.data.delivery_targets || []);
        setOtpForDev(response.data.otp_for_dev || '');
        toast.info(response.data.message || 'OTP required. Check console for dev OTP.');
        console.log('DEV OTP:', response.data.otp_for_dev);
      } else {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast.success('Login successful!');
        
        if (response.data.user.user_type === 'admin') {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Login failed';
      const status = error.response?.status ? ` (${error.response.status})` : '';
      console.error('Login failed', {
        apiBase: BASE_WITHOUT_API,
        status: error.response?.status,
        code: error.code,
        message: error.message,
        detail: error.response?.data,
      });
      toast.error(`Login failed${status}: ${detail}`, { duration: 7000 });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      //const response = await authAPI.verifyOTP({ identifier: formData.identifier, otp });
      const response = await authAPI.verifyOTP({
        identifier: otpIdentifier || formData.identifier,
        otp,
        challenge_id: otpChallengeId || undefined,
      });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Login successful!');
      
      if (response.data.user.user_type === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP', { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const startPasswordReset = () => {
    setRequiresOTP(false);
    setOtp('');
    setOtpForDev('');
    setOtpChallengeId('');
    setOtpScope('');
    setOtpDeliveryTargets([]);
    setAuthMode('reset-request');
    setResetIdentifier('');
    setResetOtpForDev('');
    setResetData({
      identifier: formData.identifier,
      otp: '',
      new_password: '',
      confirm_password: '',
    });
  };

  const returnToLogin = () => {
    setAuthMode('login');
    setRequiresOTP(false);
    setOtp('');
    setOtpForDev('');
    setOtpChallengeId('');
    setOtpScope('');
    setOtpDeliveryTargets([]);
    setResetIdentifier('');
    setResetOtpForDev('');
    setResetData({
      identifier: '',
      otp: '',
      new_password: '',
      confirm_password: '',
    });
  };

  const handleRequestPasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.forgotPassword({ identifier: resetData.identifier });
      setResetIdentifier(resetData.identifier);
      setResetOtpForDev(response.data.otp_for_dev || '');
      setAuthMode('reset-confirm');
      toast.success(response.data.message || 'Password reset OTP sent.');

      if (response.data.otp_for_dev) {
        console.log('DEV RESET OTP:', response.data.otp_for_dev);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to request password reset', { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPasswordReset = async (e) => {
    e.preventDefault();

    if (resetData.new_password !== resetData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const identifier = resetIdentifier || resetData.identifier;
      const response = await authAPI.resetPassword({
        identifier,
        otp: resetData.otp,
        new_password: resetData.new_password,
      });

      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setFormData({ identifier, password: '' });
      returnToLogin();
      toast.success(response.data.message || 'Password reset successful. You can now sign in.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to reset password', { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const switchMethod = (method) => {
    setLoginMethod(method);
    setFormData({ identifier: '', password: '' });
  };

  const cardTitle = requiresOTP
    ? 'Verify OTP'
    : authMode === 'reset-request'
      ? 'Reset Password'
      : authMode === 'reset-confirm'
        ? 'Create New Password'
        : 'Welcome Back';

  const cardDescription = requiresOTP
    ? otpScope === 'admin_login'
      ? 'Admin sign-in requires a one-time MFA code sent to your registered phone and email'
      : 'Enter the OTP sent to your registered phone'
    : authMode === 'reset-request'
      ? 'Enter your email or phone number and we will send a reset OTP via SMS'
      : authMode === 'reset-confirm'
        ? 'Enter the reset OTP and choose a new password'
        : 'Sign in to your CaddiePay account';

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-slate-100">
      <div className="relative overflow-hidden h-64 sm:h-72 lg:h-auto lg:min-h-screen lg:w-1/2">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1554895019-249ab52007f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxnb2xmJTIwYmFsbCUyMG9uJTIwZ3Jhc3MlMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc2OTQzMzQ3Mnww&ixlib=rb-4.1.0&q=85)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/90 to-emerald-700/80" />
        <div className="relative z-10 flex h-full flex-col justify-center p-8 lg:p-12 text-white">
          <h1 className="text-4xl lg:text-5xl font-bold mb-3 lg:mb-4">CaddiePay</h1>
          <p className="text-lg lg:text-xl text-emerald-100">Effortless payments for the modern golf course</p>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8 bg-gradient-to-br from-slate-50 to-emerald-50/40">
        <Card className="w-full max-w-md border-emerald-100 shadow-xl bg-white/95">
          <CardHeader>
            <CardTitle className="text-3xl text-emerald-900">{cardTitle}</CardTitle>
            <CardDescription className="text-slate-600">{cardDescription}</CardDescription>
          </CardHeader>
          <CardContent>
            {accessNotice && (
              <p role="alert" className="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                {accessNotice}
              </p>
            )}
            {authMode === 'login' && !requiresOTP ? (
              <>
                <div className="flex rounded-lg border border-slate-200 p-1 mb-6" data-testid="login-method-toggle">
                  <button
                    type="button"
                    onClick={() => switchMethod('email')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${
                      loginMethod === 'email'
                        ? 'bg-emerald-800 text-white'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                    data-testid="login-method-email-btn"
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => switchMethod('phone')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${
                      loginMethod === 'phone'
                        ? 'bg-emerald-800 text-white'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                    data-testid="login-method-phone-btn"
                  >
                    <Phone className="w-4 h-4" />
                    Phone
                  </button>
                </div>

                <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
                  <div className="space-y-2">
                    {loginMethod === 'email' ? (
                      <>
                        <Label htmlFor="identifier" className="text-sm font-semibold text-slate-700">Email</Label>
                        <Input
                          id="identifier"
                          type="email"
                          placeholder="golfer@example.com"
                          value={formData.identifier}
                          onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                          required
                          className="input-field"
                          data-testid="email-input"
                        />
                      </>
                    ) : (
                      <>
                        <Label htmlFor="identifier" className="text-sm font-semibold text-slate-700">Phone Number</Label>
                        <Input
                          id="identifier"
                          type="tel"
                          placeholder="+27821234567"
                          value={formData.identifier}
                          onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                          required
                          className="input-field"
                          data-testid="phone-input"
                        />
                      </>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-semibold text-slate-700">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="input-field"
                      data-testid="password-input"
                    />
                    <div className="text-right">
                      <button
                        type="button"
                        onClick={startPasswordReset}
                        className="text-sm font-semibold text-emerald-900 hover:underline"
                        data-testid="forgot-password-btn"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="login-submit-btn">
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </>
            ) : authMode === 'reset-request' ? (
              <form onSubmit={handleRequestPasswordReset} className="space-y-6" data-testid="forgot-password-form">
                <div className="space-y-2">
                  <Label htmlFor="reset-identifier" className="text-sm font-semibold text-slate-700">
                    Email or Phone Number
                  </Label>
                  <Input
                    id="reset-identifier"
                    type="text"
                    placeholder="golfer@example.com or +27821234567"
                    value={resetData.identifier}
                    onChange={(e) => setResetData({ ...resetData, identifier: e.target.value })}
                    required
                    className="input-field"
                    data-testid="reset-identifier-input"
                  />
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="send-reset-otp-btn">
                  {loading ? 'Sending OTP...' : 'Send Reset OTP'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={returnToLogin}
                  disabled={loading}
                  data-testid="back-to-login-btn"
                >
                  Back to Sign In
                </Button>
              </form>
            ) : authMode === 'reset-confirm' ? (
              <form onSubmit={handleConfirmPasswordReset} className="space-y-5" data-testid="reset-password-form">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-sm text-emerald-900 font-medium">Password reset OTP sent via SMS</p>
                  {resetIdentifier && (
                    <p className="text-xs text-emerald-700 mt-1">Request for: <span className="font-semibold">{resetIdentifier}</span></p>
                  )}
                  {resetOtpForDev && (
                    <p className="text-xs text-emerald-700 mt-2">Dev OTP: <span className="font-mono font-bold">{resetOtpForDev}</span></p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-otp" className="text-sm font-semibold text-slate-700">Reset OTP</Label>
                  <Input
                    id="reset-otp"
                    type="text"
                    placeholder="123456"
                    value={resetData.otp}
                    onChange={(e) => setResetData({ ...resetData, otp: e.target.value })}
                    required
                    maxLength={6}
                    className="input-field font-mono text-center text-2xl tracking-widest"
                    data-testid="reset-otp-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-sm font-semibold text-slate-700">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="At least 6 characters"
                    value={resetData.new_password}
                    onChange={(e) => setResetData({ ...resetData, new_password: e.target.value })}
                    required
                    minLength={6}
                    className="input-field"
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-sm font-semibold text-slate-700">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Repeat new password"
                    value={resetData.confirm_password}
                    onChange={(e) => setResetData({ ...resetData, confirm_password: e.target.value })}
                    required
                    minLength={6}
                    className="input-field"
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="reset-password-submit-btn">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleRequestPasswordReset}
                    disabled={loading}
                    data-testid="resend-reset-otp-btn"
                  >
                    Resend OTP
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={returnToLogin}
                    disabled={loading}
                    data-testid="back-to-login-confirm-btn"
                  >
                    Back to Sign In
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-6" data-testid="otp-form">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-sm text-emerald-900 font-medium">
                    {otpScope === 'admin_login' ? 'Admin MFA code sent to your registered phone and email' : 'OTP sent to your registered device'}
                  </p>
                  {otpScope === 'admin_login' && otpDeliveryTargets.length ? (
                    <p className="text-xs text-emerald-700 mt-1">
                      Sent to: <span className="font-semibold">{otpDeliveryTargets.join(' and ')}</span>
                    </p>
                  ) : otpIdentifier ? (
                    <p className="text-xs text-emerald-700 mt-1">Sent to: <span className="font-semibold">{otpIdentifier}</span></p>
                  ) : null}
                  {otpForDev && (
                    <p className="text-xs text-emerald-700 mt-2">Dev OTP: <span className="font-mono font-bold">{otpForDev}</span></p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-sm font-semibold text-slate-700">Enter OTP</Label>
                  <Input
                    id="otp"
                    type="text"
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    maxLength={6}
                    className="input-field font-mono text-center text-2xl tracking-widest"
                    data-testid="otp-input"
                  />
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="verify-otp-btn">
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={returnToLogin}
                  disabled={loading}
                  data-testid="otp-back-to-login-btn"
                >
                  Back to Sign In
                </Button>
              </form>
            )}

            {authMode === 'login' && !requiresOTP && (
              <div className="mt-6 text-center">
              <p className="text-sm text-slate-600">
                Don't have an account?{' '}
                <Link to="/register" className="text-emerald-900 font-semibold hover:underline">
                  Sign up
                </Link>
              </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Login;
